<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceReferenceSearch',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoiceId: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onReferenceSelected = (...args) => {
    const { option, value } = args[0];
    if (option) {
      selectedItems.value.push(option);

      if (option.rental_id && option.rental_name) {
        selectedItems.value.push({
          object: 'rental',
          id: option.rental_id,
          name: option.rental_name,
        });
      }

      if (option.client_id && option.client_name) {
        selectedItems.value.push({
          object: 'company',
          id: option.client_id,
          name: option.client_name,
        });
      }
      if (option.properties) {
        _.each(option.properties, (p) => {
          if (p.object == 'rental_property' && !p.inactive && p.property_unit) {
            selectedItems.value.push({
              object: 'property_unit',
              id: p.property_unit.id,
              name: p.property_unit.full_name || p.property_unit.full_code,
            });
          }
        });
      }
    }
  };

  const formId = ref(null);
  const searchTerm = ref(null);
  const items = ref([]);
  const selectedItems = ref([]);
  const referenceTypes = ref(null);
  const selectedReferenceType = ref(null);
  const selectedReference = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_7789867021953041_clearable = computed(() => false);

  const $data = computed(() => ({
    formId: formId.value,
    searchTerm: searchTerm.value,
    items: items.value,
    selectedItems: selectedItems.value,
    referenceTypes: referenceTypes.value,
    selectedReferenceType: selectedReferenceType.value,
    selectedReference: selectedReference.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_clearable: b_7789867021953041_clearable.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_referenceTypes = async () => {
    return [
      {
        type: 'contract',
        text: $fn.value.gettext('Contracts'),
        target: 'contracts',
        dataSource: {
          name: 'suggest_contract',
          parameters: {},
        },
        template: '',
      },
      {
        type: 'rental',
        text: $fn.value.gettext('Rentals'),
        target: 'rentals',
        dataSource: {
          name: 'suggest_rental',
          parameters: {},
        },
      },
      {
        type: 'client',
        text: $fn.value.gettext('Clients'),
        target: 'tenants',
        dataSource: {
          name: 'suggest_company',
          parameters: {
            role: 'partner',
          },
        },
      },
      {
        type: 'property',
        text: $fn.value.gettext('Properties'),
        target: 'properties',
        dataSource: {
          name: 'suggest_property',
          parameters: {
            types: 'property,unit',
          },
        },
      },
    ];
  };
  const $$load_selectedReference = async () => {
    return (
      _.find(referenceTypes.value, { type: selectedReferenceType.value }) ||
      referenceTypes.value[0]
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'searchTerm' },
      { name: 'items' },
      { name: 'selectedItems' },
      { name: 'referenceTypes' },
      {
        name: 'selectedReferenceType',
        cache: {
          type: 'local',
          key: 'InvoiceReferenceSearchModal.selectedReferenceType',
        },
      },
      { name: 'selectedReference', dependsOn: ['referenceTypes'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7789867021953041_clearable', nodeId: '7789867021953041' },
    ],
    loaders: {
      formId: $$load_formId,
      referenceTypes: $$load_referenceTypes,
      selectedReference: $$load_selectedReference,
    },
    ux: {},
    dataRefs: {
      formId,
      searchTerm,
      items,
      selectedItems,
      referenceTypes,
      selectedReferenceType,
      selectedReference,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_clearable,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Invoice', 'Search Reference') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoiceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoiceId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0c8ugfo9k_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_setData_tii0yf1o8_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'searchTerm',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_setData_pv6c6v077_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'items',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_setData_b0qdoc7ui_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedReferenceType',
      value: $event.data.value.type,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3468003412020058_script_fq145gsap_value_callable = async (
    $event
  ) => {
    return onReferenceSelected($event.data);
  };
  const act_3468003412020058_script_fq145gsap_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3468003412020058_script_fq145gsap_value_callable($event);
      },
    };
  };
  const act_3468003412020058_setData_4ky4a4t4z_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'searchTerm',
      value: null,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3468003412020058_setData_m2hnc09h4_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'items',
      value: [],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6049458597007392_setData_q8iqc81m0_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value:
        (_.remove(selectedItems.value, $event.scope.item),
        selectedItems.value.slice()),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2140198529698299_closeComponent_6ob3qb61s_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_x1318x2rt_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_invoice_reference',
      data: _.map(selectedItems.value, (p) => ({ reference_id: p.id })),
      parameters: {
        invoice_id: props.invoiceId,
      },
    };
  };

  const act_7315092382398562_closeModal_0c8ugfo9k =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0c8ugfo9k_getActionArgs,
    });
  const inst_evh_7315092382398562_close_5np8u2puz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0c8ugfo9k,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_5np8u2puz],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_pbeonw1b5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_mg3b5i814 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_pbeonw1b5,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_mg3b5i814],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_eb1j7n6zr =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_42unp6zpb =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_eb1j7n6zr,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_42unp6zpb],
    event: 'close',
  });
  const act_7789867021953041_setData_tii0yf1o8 = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_tii0yf1o8_getActionArgs,
  });
  const inst_evh_7789867021953041_optionSelected_w09z1ca8p =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_tii0yf1o8,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_7789867021953041_setData_pv6c6v077 = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_pv6c6v077_getActionArgs,
  });
  const inst_evh_7789867021953041_optionSelected_7bf3930vb =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_pv6c6v077,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_7789867021953041_setData_b0qdoc7ui = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_b0qdoc7ui_getActionArgs,
  });
  const inst_evh_7789867021953041_optionSelected_7f88cx2ew =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_b0qdoc7ui,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_7789867021953041_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7789867021953041_optionSelected_w09z1ca8p,
        inst_evh_7789867021953041_optionSelected_7bf3930vb,
        inst_evh_7789867021953041_optionSelected_7f88cx2ew,
      ],
      event: 'optionSelected',
    });
  const act_3468003412020058_script_fq145gsap = new actionTypes.ScriptAction({
    actionArgs: act_3468003412020058_script_fq145gsap_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_yq7g67q7b =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_script_fq145gsap,
      event: 'optionSelected',
      displayName: 'script',
    });
  const act_3468003412020058_setData_4ky4a4t4z = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_4ky4a4t4z_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_kks6tyyod =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_4ky4a4t4z,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_3468003412020058_setData_m2hnc09h4 = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_m2hnc09h4_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_q3occywgy =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_m2hnc09h4,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_3468003412020058_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_3468003412020058_optionSelected_yq7g67q7b,
        inst_evh_3468003412020058_optionSelected_kks6tyyod,
        inst_evh_3468003412020058_optionSelected_q3occywgy,
      ],
      event: 'optionSelected',
    });
  const act_6049458597007392_setData_q8iqc81m0 = new actionTypes.SetDataAction({
    actionArgs: act_6049458597007392_setData_q8iqc81m0_getActionArgs,
  });
  const inst_evh_6049458597007392_clickPreventStop_oma0t14d4 =
    new actionTypes.EventHandlerImpl({
      action: act_6049458597007392_setData_q8iqc81m0,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_6049458597007392_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6049458597007392_clickPreventStop_oma0t14d4],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeComponent_6ob3qb61s =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2140198529698299_closeComponent_6ob3qb61s_getActionArgs,
    });
  const inst_evh_2140198529698299_success_l8cqsa7nl =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_6ob3qb61s,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2140198529698299_request_x1318x2rt = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_x1318x2rt_getActionArgs,
    displayName: 'submitReferences',
    events: [inst_evh_2140198529698299_success_l8cqsa7nl],
  });
  const inst_evh_2140198529698299_clickPreventStop_yk6ynbv0q =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_x1318x2rt,
      event: 'click.prevent.stop',
      displayName: 'submitReferences',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_yk6ynbv0q],
      event: 'click.prevent.stop',
    });
  const act_2434514588044777_closeComponent_5zmotprg4 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_687u28qts =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_5zmotprg4,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_687u28qts],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_5np8u2puz,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_mg3b5i814,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_42unp6zpb,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_optionSelected_w09z1ca8p,
    inst_evh_7789867021953041_optionSelected_7bf3930vb,
    inst_evh_7789867021953041_optionSelected_7f88cx2ew,
    inst_evh_7789867021953041_optionSelected,
    inst_evh_3468003412020058_optionSelected_yq7g67q7b,
    inst_evh_3468003412020058_optionSelected_kks6tyyod,
    inst_evh_3468003412020058_optionSelected_q3occywgy,
    inst_evh_3468003412020058_optionSelected,
    inst_evh_6049458597007392_clickPreventStop_oma0t14d4,
    inst_evh_6049458597007392_clickPreventStop,
    inst_evh_2140198529698299_success_l8cqsa7nl,
    inst_evh_2140198529698299_clickPreventStop_yk6ynbv0q,
    submitReferences: inst_evh_2140198529698299_clickPreventStop_yk6ynbv0q,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2434514588044777_clickPreventStop_687u28qts,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_5np8u2puz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_5np8u2puz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_mg3b5i814 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_mg3b5i814.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_42unp6zpb = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_42unp6zpb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_w09z1ca8p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_w09z1ca8p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_7bf3930vb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_7bf3930vb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_7f88cx2ew = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_7f88cx2ew.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_yq7g67q7b = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_yq7g67q7b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_kks6tyyod = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_kks6tyyod.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_q3occywgy = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_q3occywgy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3468003412020058_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6049458597007392_clickPreventStop_oma0t14d4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6049458597007392_clickPreventStop_oma0t14d4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6049458597007392_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6049458597007392_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_l8cqsa7nl = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_l8cqsa7nl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_yk6ynbv0q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_yk6ynbv0q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_687u28qts = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_687u28qts.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceReferenceSearch"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm name="search" :id="formId">
        <div class="row">
          <ItpSelect2
            name="selectedReference"
            v-model="selectedReference"
            :value="selectedReference"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (selectedReference =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="referenceTypes"
            valueField="target"
            textField="text"
            :clearable="b_7789867021953041_clearable"
            bind-object
            class="col-4"
            @optionSelected="evh_7789867021953041_optionSelected($event, {})"
          >
          </ItpSelect2>
          <ItpSelect2
            name="search"
            v-model="searchTerm"
            :value="searchTerm"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (searchTerm =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :placeholder="pgettext('Search Placeholder', 'Search...')"
            :dataSource="selectedReference && selectedReference.dataSource"
            :options="items"
            valueField="id"
            bind-object
            optionTemplate="<InvoiceReferenceSearchOption :option=option>
</InvoiceReferenceSearchOption>"
            use-option-template-for-selected-option
            class="col-8"
            @optionSelected="evh_3468003412020058_optionSelected($event, {})"
          >
          </ItpSelect2>
        </div>
        <BListGroup class="mt-3">
          <BListGroupItem :key="item.id" v-for="item in selectedItems">
            <ItpBox alignItems="start">
              <InvoiceReferenceItem :item="item" no-link>
              </InvoiceReferenceItem>
              <button
                :aria-label="pgettext('InvoiceReferenceSearch', 'Remove')"
                type="button"
                class="btn-close ms-auto me-1"
                @click.prevent.stop="
                  evh_6049458597007392_clickPreventStop($event, { item })
                "
              ></button>
            </ItpBox>
          </BListGroupItem>
        </BListGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('InvoiceReferenceSearch/Submit', 'Add references')"
          variant="primary"
          icon="fa-save"
          :spinning="
            actions.submitReferences && actions.submitReferences.isRunning
          "
          :disabled="!selectedItems.length"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="
            (actions.search && actions.search.isRunning) ||
            (actions.submitReferences && actions.submitReferences.isRunning)
          "
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
